import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  getDrawingDimFontPx,
  measureLiveDimensionOverlays,
} from '../lib/drawingDimensionOverlays';
import {
  prepareSvgHtmlForEmbed,
  prepareSvgHtmlForDisplay,
  svgHtmlToDataUrl,
} from '../lib/svgCapture';

const VmkQuoteDrawing = ({ svgHtml, width, height, compact = false, skipDimensionOverlays = false }) => {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [overlays, setOverlays] = useState([]);
  const [displayDataUrl, setDisplayDataUrl] = useState(null);
  const [imgReady, setImgReady] = useState(false);
  const fontPx = getDrawingDimFontPx(compact);

  const preparedSvg = useMemo(
    () => (svgHtml ? prepareSvgHtmlForEmbed(svgHtml, { saved: true }) : null),
    [svgHtml]
  );

  useEffect(() => {
    if (!preparedSvg) {
      setDisplayDataUrl(null);
      setImgReady(false);
      return undefined;
    }

    let cancelled = false;
    setImgReady(false);

    prepareSvgHtmlForDisplay(preparedSvg, { saved: true }).then((readySvg) => {
      if (cancelled) return;
      setDisplayDataUrl(svgHtmlToDataUrl(readySvg || preparedSvg, { saved: true }));
    });

    return () => {
      cancelled = true;
    };
  }, [preparedSvg]);

  const updateOverlays = useCallback(() => {
    const wrap = wrapRef.current;
    const img = imgRef.current;
    const svg = wrap?.querySelector('svg');

    if (skipDimensionOverlays || !wrap || !preparedSvg || !width || !height || !svg) {
      setOverlays([]);
      return;
    }

    if (img && (!imgReady || img.clientWidth < 1)) {
      return;
    }

    setOverlays(measureLiveDimensionOverlays(wrap, svg, width, height, img || null));
  }, [preparedSvg, width, height, skipDimensionOverlays, imgReady]);

  useLayoutEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setImgReady(true);
    }
  }, [displayDataUrl]);

  useLayoutEffect(() => {
    updateOverlays();
    requestAnimationFrame(updateOverlays);

    const wrap = wrapRef.current;
    const img = imgRef.current;
    if (!wrap) return undefined;

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(updateOverlays);
    });
    observer.observe(wrap);
    if (img) observer.observe(img);

    const svg = wrap.querySelector('svg');
    if (svg) observer.observe(svg);

    return () => observer.disconnect();
  }, [updateOverlays, displayDataUrl]);

  const handleImgLoad = () => {
    setImgReady(true);
  };

  if (!preparedSvg) return null;

  return (
    <div ref={wrapRef} className="vmk-drawing-wrap relative w-full">
      <div className="vmk-drawing-inner w-full flex justify-center items-start relative">
        {displayDataUrl ? (
          <img
            ref={imgRef}
            src={displayDataUrl}
            alt=""
            className="vmk-drawing-img max-w-full max-h-[420px] w-auto h-auto object-contain"
            draggable={false}
            onLoad={handleImgLoad}
          />
        ) : (
          <div className="vmk-drawing-img-placeholder w-full min-h-[120px]" aria-hidden />
        )}
        {!skipDimensionOverlays ? (
          <div
            className="vmk-drawing-measure absolute w-0 h-0 overflow-hidden opacity-0 pointer-events-none"
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: preparedSvg }}
          />
        ) : null}
      </div>
      {overlays.length > 0 && (
        <div className="vmk-dim-labels" aria-hidden="true">
          {overlays.map((overlay) => (
            <span
              key={overlay.id}
              className={`vmk-dim-label-overlay${overlay.align === 'start' ? ' vmk-dim-label-overlay--height' : ''}`}
              style={{
                left: `${overlay.left}px`,
                top: `${overlay.top}px`,
                fontSize: `${fontPx}px`,
              }}
            >
              {overlay.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default VmkQuoteDrawing;
